import React, { useState, useEffect, useMemo } from "react";
import { Link, Outlet, useNavigate } from "react-router-dom";
import {
  Bars3Icon,
  XMarkIcon,
  ArrowRightOnRectangleIcon,
} from "@heroicons/react/24/outline";
import CreateButton from "../ui/CreateButton";
import NotificationButton from "../ui/NotificationButton";
import SettingButton from "../ui/SettingButton";
import HelpButton from "../ui/HelpButton";
import { SIDEBAR_NAV } from "../../config/sidebarNav";
import { canAccess, getUserRole } from "../../utils/permissions";

export default function UserProfile() {
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);

  const toggleSidebar = () => setIsSidebarOpen((prev) => !prev);

  const handleLogout = () => {
    localStorage.removeItem("company_token");
    localStorage.removeItem("user_data");
    localStorage.removeItem("user_role");
    localStorage.removeItem("user_permissions");
    localStorage.clear();
    sessionStorage.clear();
    navigate("/company/login");
  };

  useEffect(() => {
    if (window.innerWidth >= 1024) setIsSidebarOpen(true);
    else setIsSidebarOpen(false);
  }, []);

  const filteredNavGroups = useMemo(() => {
    const role = getUserRole();
    return SIDEBAR_NAV.map((group) => ({
      ...group,
      items: group.items.filter((item) => {
        if (item.path === "") {
          return role === "company";
        }
        return canAccess(item.permission);
      }),
    })).filter((group) => group.items.length > 0);
  }, []);

  const canShowCreate = useMemo(() => {
    return (
      canAccess("create_client") ||
      canAccess("create_job") ||
      canAccess("create_quote")
    );
  }, []);

  return (
    <div className="flex min-h-screen">
      <aside
        className={`fixed inset-y-0 left-0 w-[17rem] bg-white shadow-xl border-r border-gray-200 transform transition-transform duration-300 z-50
                ${isSidebarOpen ? "translate-x-0" : "-translate-x-full"}`}
      >
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between p-6 border-b border-gray-100">
            <div className="flex items-center gap-3">
              <div className="w-[3rem] h-[3rem] rounded-lg flex items-center justify-center p-1">
                <img
                  src={require("../../assets/logo/logo.svg").default}
                  alt="Logo"
                  className="w-full h-full"
                />
              </div>
              <h1 className="text-xl font-bold bg-gradient-to-r from-[#1665f6] to-[#8830b8] bg-clip-text text-transparent">
                Company Portal
              </h1>
            </div>
            <button
              onClick={toggleSidebar}
              className="text-gray-600 lg:hidden hover:text-gray-800"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>

          <nav className="flex-1 p-4 space-y-8 overflow-y-auto custom-scrollbar">
            {filteredNavGroups.map((group, groupIndex) => (
              <div key={group.title}>
                <div className="space-y-1">
                  {group.items.map((item, idx) => {
                    const Icon = item.icon;
                    // Use a unique key: path + label + idx
                    const uniqueKey = `${item.path || "root"}-${item.label
                      }-${idx}`;
                    return (
                      <Link
                        key={uniqueKey}
                        to={item.path}
                        className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-gray-700 hover:bg-[#1665f6]/10 hover:text-[#1665f6] transition-all duration-200 group"
                        onClick={() =>
                          window.innerWidth < 1024 && setIsSidebarOpen(false)
                        }
                      >
                        <Icon className="w-5 h-5 text-gray-500 group-hover:text-[#1665f6]" />
                        <span className="text-sm font-medium">
                          {item.label}
                        </span>
                      </Link>
                    );
                  })}
                </div>

                {groupIndex < filteredNavGroups.length - 1 && (
                  <div className="h-px my-1 bg-gray-200" />
                )}
              </div>
            ))}
          </nav>

          <div className="p-4 border-t border-gray-200">
            <button
              onClick={handleLogout}
              className="flex items-center w-full gap-3 px-4 py-3 font-medium text-red-600 transition rounded-lg hover:bg-red-50"
            >
              <ArrowRightOnRectangleIcon className="w-5 h-5" />
              Logout
            </button>
          </div>
        </div>
      </aside>

      <div
        className={`flex-1 transition-all duration-300 ${isSidebarOpen ? "lg:ml-[17rem]" : ""
          }`}
      >
        <header className="sticky top-0 z-40 flex items-center justify-between px-6 py-4 bg-white border-b border-gray-200 shadow-sm">
          <div className="flex items-center gap-[15px]">
            <button
              onClick={toggleSidebar}
              className="p-2 rounded-lg hover:bg-gray-100 text-gray-600 hover:text-[#1665f6] transition"
            >
              {isSidebarOpen && window.innerWidth < 1024 ? (
                <XMarkIcon className="w-6 h-6" />
              ) : (
                <Bars3Icon className="w-6 h-6" />
              )}
            </button>
            {canShowCreate && <CreateButton />}
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-3">
              <NotificationButton />
              <HelpButton />
              <SettingButton />
            </div>
          </div>
        </header>

        <main className="min-h-screen p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
